import posthog from '../../posthog';
import CONSTANTS from 'shared/constants';

// Staff-action events for the Appointments area (/schedules and the appointment modal
// opened from /task-manager). Identification (person, company group) is attached by
// use-analytics-identity, so callers only pass event-specific properties plus asCompany.
// Shared so CIN-7081 / CIN-6978 reuse the same is_plg + company_id derivation.
export const SCHEDULER_EVENTS = {
  APPOINTMENTS_TAB_VIEWED: 'scheduler_appointments_tab_viewed',
  APPOINTMENT_CREATED_MANUALLY: 'scheduler_appointment_created_manually',
  APPOINTMENT_UPDATED: 'scheduler_appointment_updated',
  APPOINTMENT_CANCELLED_BY_STAFF: 'scheduler_appointment_cancelled_by_staff',
  APPOINTMENT_DELETED: 'scheduler_appointment_deleted',
  TIME_BLOCKED: 'scheduler_time_blocked',
  TIME_BLOCK_DELETED: 'scheduler_time_block_deleted',
  LEAD_BOOKED: 'scheduler_lead_booked',
  LEAD_DELETED: 'scheduler_lead_deleted',
  WRITEBACK_RETRY_CLICKED: 'scheduler_writeback_retry_clicked',
  REPORT_EXPORTED: 'scheduler_report_exported',
  // CIN-7081: Scheduler setup (builder, PLG wizard), install, edit-page upkeep, lifecycle.
  CALENDAR_CREATED: 'scheduler_calendar_created',
  BUILDER_STEP_REACHED: 'scheduler_builder_step_reached',
  BUILDER_STEP_COMPLETED: 'scheduler_builder_step_completed',
  CALENDAR_ACTIVATED: 'scheduler_calendar_activated',
  BOOKING_LINK_COPIED: 'scheduler_booking_link_copied',
  TRACKING_LINK_CREATED: 'scheduler_tracking_link_created',
  GOOGLE_LISTING_TOGGLED: 'scheduler_google_listing_toggled',
  EDIT_TAB_VIEWED: 'scheduler_edit_tab_viewed',
  CALENDAR_SETTINGS_SAVED: 'scheduler_calendar_settings_saved',
  CALENDAR_SETTINGS_SAVE_FAILED: 'scheduler_calendar_settings_save_failed',
  LOCATION_OVERRIDE_SAVED: 'scheduler_location_override_saved',
  CALENDAR_PREVIEWED: 'scheduler_calendar_previewed',
  CALENDAR_DELETED: 'scheduler_calendar_deleted',
  COMPANY_SERVICE_CREATED: 'scheduler_company_service_created',
  COMPANY_SERVICE_UPDATED: 'scheduler_company_service_updated',
  COMPANY_SERVICE_DELETED: 'scheduler_company_service_deleted',
  COMPANY_SERVICE_REORDERED: 'scheduler_company_service_reordered',
  WIZARD_STARTED: 'scheduler_wizard_started',
  WIZARD_SCAN_CANCELLED: 'scheduler_wizard_scan_cancelled',
  WIZARD_SCAN_SUCCEEDED: 'scheduler_wizard_scan_succeeded',
  WIZARD_SCAN_FAILED: 'scheduler_wizard_scan_failed',
  WIZARD_MANUAL_SETUP: 'scheduler_wizard_manual_setup',
  WIZARD_STEP_COMPLETED: 'scheduler_wizard_step_completed',
  WIZARD_COMPLETED: 'scheduler_wizard_completed',
  WIZARD_DISMISSED: 'scheduler_wizard_dismissed'
};

// HTTP status of a failed request (axios error), or null for a non-HTTP throw.
export const errorStatusCode = error => error?.response?.status ?? null;

// On a hard refresh asCompany is the cookie stub ({ id }) until the full record lands, and any
// event captured before then reads is_plg false. The stub and the ?view=quick { id, name } object
// have no is_active; every full record does (user-state-context refetches on the same field).
// account_type can't be the signal: the API omits it entirely for a company with no account.
export const isCompanyRecordLoaded = asCompany => asCompany?.is_active !== undefined;

// Events captured against the stub wait here, tagged with the stub's company id and capture
// time (oldest dropped past the cap), until use-analytics-identity flushes them after the company
// group is set. Only entries for the loaded company are sent, at their original time; the rest
// are dropped. A stale stub for an already-loaded company sends at once with that record.
const MAX_PENDING_EVENTS = 20;
let pendingEvents = [];
let loadedCompany = null;
const sendSchedulerEvent = (event, asCompany, properties, options) => {
  // Raw account_type, not the user-state-context PLG flag (that one is true while loading).
  // company_id is the UUID string (cookie stub or full record).
  const payload = {
    company_id: asCompany?.id == null ? null : String(asCompany.id),
    is_plg: asCompany?.account_type === 'PLG',
    ...properties
  };
  // PostHog is never initialized on LOCAL hosts (src/posthog.js), so capture() is a silent
  // no-op there. This dev-only line is the observable for Cypress and the demo harness.
  if (CONSTANTS.ENV === 'LOCAL') {
    console.debug('[posthog]', event, payload);
  }
  posthog.capture(event, payload, options);
};

// Never throws: several callers sit inside a request's success path, where a throw would
// be routed into the existing error branch for a save that did persist.
export const captureSchedulerEvent = (event, {
  asCompany,
  ...properties
} = {}) => {
  try {
    if (asCompany?.id != null && !isCompanyRecordLoaded(asCompany)) {
      const companyId = String(asCompany.id);
      if (loadedCompany && String(loadedCompany.id) === companyId) {
        sendSchedulerEvent(event, loadedCompany, properties);
        return;
      }
      if (pendingEvents.length >= MAX_PENDING_EVENTS) {
        console.warn('captureSchedulerEvent queue full, dropping', pendingEvents.shift().event);
      }
      pendingEvents.push({
        event,
        properties,
        companyId,
        capturedAt: Date.now()
      });
      return;
    }
    sendSchedulerEvent(event, asCompany, properties);
  } catch (e) {
    console.warn('captureSchedulerEvent failed', event, e);
  }
};
export const flushPendingSchedulerEvents = asCompany => {
  if (!isCompanyRecordLoaded(asCompany)) return;
  loadedCompany = asCompany;
  if (!pendingEvents.length) return;
  const companyId = String(asCompany.id);
  const queued = pendingEvents;
  pendingEvents = [];
  const matching = queued.filter(entry => entry.companyId === companyId);
  if (matching.length < queued.length) {
    console.warn('captureSchedulerEvent dropped held events for another company:', queued.length - matching.length);
  }
  matching.forEach(({
    event,
    properties,
    capturedAt
  }) => {
    try {
      sendSchedulerEvent(event, asCompany, properties, {
        timestamp: new Date(capturedAt)
      });
    } catch (e) {
      console.warn('captureSchedulerEvent failed', event, e);
    }
  });
};
export const clearPendingSchedulerEvents = () => {
  pendingEvents = [];
  loadedCompany = null;
};